// animation-utils.ts
import { VideoData } from "./types";
import { animate } from "popmotion";
import { linear, easeInOut, easeOut, anticipate, backOut } from "popmotion";
import { cubicBezier } from "@popmotion/easing";

// Easing functions
export const Easing = {
  linear,
  easeInOutQuad: easeInOut,
  easeInOutCubic: cubicBezier(0.645, 0.045, 0.355, 1.0),
  easeInOutSine: cubicBezier(0.445, 0.05, 0.55, 0.95),
  spring: backOut,
  velocityBased: cubicBezier(0.16, 0.01, 0.5, 0.99),
  balancedSpring: anticipate,
};

export type EasingFunction = (t: number) => number;

export interface AnimationOptions {
  fps?: number;
  transitionDuration?: number;
  easing?: EasingFunction;
  onFrameChange: (frameName: string) => void;
  onComplete: () => void;
  adaptiveSpeed?: boolean;
  maxDuration?: number;
  frameSkipRate?: number;
}

// Extract frame number from frame name
export const getFrameNumber = (frameName: string): number => {
  return parseInt(frameName.match(/\d+/)?.[0] || "0");
};

// Generate frame name from number
export const getFrameName = (frameNumber: number, prefix: string, digits: number, extension: string): string => {
  return `${prefix}${frameNumber.toString().padStart(digits, "0")}${extension}`;
};

// Animation function to transition between frames
export function animateFrames(
  startFrameName: string,
  endFrameName: string,
  direction: "forward" | "backward",
  totalFrames: number,
  frameNameGenerator: (num: number) => string,
  options: AnimationOptions,
) {
  // Parse frame numbers
  const startFrame = getFrameNumber(startFrameName);
  const endFrame = getFrameNumber(endFrameName);
  const skipRate = options.frameSkipRate || 1;

  // Calculate total frames to animate without generating the full sequence
  let totalFramesToAnimate: number;

  if (direction === "forward") {
    if (endFrame > startFrame) {
      totalFramesToAnimate = Math.ceil((endFrame - startFrame) / skipRate);
    } else {
      // Wrap around case
      totalFramesToAnimate = Math.ceil((totalFrames - startFrame + endFrame) / skipRate);
    }
  } else {
    if (startFrame > endFrame) {
      totalFramesToAnimate = Math.ceil((startFrame - endFrame) / skipRate);
    } else {
      // Wrap around case
      totalFramesToAnimate = Math.ceil((startFrame + (totalFrames - endFrame)) / skipRate);
    }
  }

  if (totalFramesToAnimate === 0) {
    options.onComplete();
    return () => {};
  }

  // Function to calculate frame number at given progress
  const getFrameAtProgress = (progress: number): number => {
    const frameIndex = Math.min(Math.floor(progress * totalFramesToAnimate), totalFramesToAnimate - 1);

    if (direction === "forward") {
      if (endFrame > startFrame) {
        return Math.min(startFrame + (frameIndex + 1) * skipRate, endFrame);
      } else {
        // Wrap around case
        const totalSteps = startFrame + (frameIndex + 1) * skipRate;
        if (totalSteps <= totalFrames) {
          return totalSteps;
        } else {
          return totalSteps - totalFrames;
        }
      }
    } else {
      if (startFrame > endFrame) {
        return Math.max(startFrame - (frameIndex + 1) * skipRate, endFrame);
      } else {
        // Wrap around case
        const totalSteps = startFrame - (frameIndex + 1) * skipRate;
        if (totalSteps >= 1) {
          return totalSteps;
        } else {
          return totalFrames + totalSteps;
        }
      }
    }
  };

  // Calculate optimal duration
  const baseFps = options.fps || 24;
  const idealDuration = totalFramesToAnimate * (1000 / baseFps);
  const minDuration = 300;
  const maxDuration = options.maxDuration || 2500;
  const transitionDuration = options.adaptiveSpeed
    ? Math.min(Math.max(idealDuration, minDuration), maxDuration)
    : options.transitionDuration || 800;

  // Setup animation control
  let animationCancel: () => void;
  let lastFrameNum = -1;

  // Use popmotion's animate function
  animationCancel = animate({
    from: 0,
    to: 1,
    duration: transitionDuration,
    ease: options.easing || Easing.balancedSpring,
    onUpdate: (progress) => {
      const frameNum = getFrameAtProgress(progress);

      // Only update when the frame changes
      if (frameNum !== lastFrameNum) {
        const frameName = frameNameGenerator(frameNum);
        options.onFrameChange(frameName);
        lastFrameNum = frameNum;
      }
    },
    onComplete: () => {
      // Ensure we show the final frame
      const finalFrameName = frameNameGenerator(endFrame);
      options.onFrameChange(finalFrameName);
      options.onComplete();
    },
  }).stop;

  // Return function to cancel animation
  return () => {
    if (animationCancel) {
      animationCancel();
    }
  };
}
